import React, { useEffect, useRef, useState } from "react";
import { Box, Typography, Button, Link as MUILink } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";

const CONSENT_KEY = "cookieConsent";

const CookieConsent = () => {
  const navigate = useNavigate();
  const bannerRef = useRef(null);
  const [visible, setVisible] = useState(() => {
    try {
      return !localStorage.getItem(CONSENT_KEY);
    } catch {
      return true;
    }
  });

  useEffect(() => {
    if (!visible) {
      document.body.style.paddingBottom = "";
      return;
    }
    const updatePadding = () => {
      if (bannerRef.current) {
        document.body.style.paddingBottom = `${bannerRef.current.offsetHeight}px`;
      }
    };
    updatePadding();
    window.addEventListener("resize", updatePadding);
    return () => {
      window.removeEventListener("resize", updatePadding);
      document.body.style.paddingBottom = "";
    };
  }, [visible]);

  const saveConsent = (status) => {
    try {
      localStorage.setItem(
        CONSENT_KEY,
        JSON.stringify({ status, date: new Date().toISOString() })
      );
    } catch {
      // ignore storage errors (e.g. private browsing)
    }
  };

  const handleAccept = () => {
    saveConsent("accepted");
    setVisible(false);
    window.location.reload();
  };

  const handleReject = () => {
    saveConsent("rejected");
    setVisible(false);
    window.location.reload();
  };

  const handleCustomize = () => {
    navigate("/cookie-policy");
  };

  if (!visible) return null;

  return (
    <Box
      ref={bannerRef}
      role="dialog"
      aria-label="Cookie consent"
      sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1500,
        backgroundColor: "#fff",
        borderTop: "1px solid #e0e0e0",
        boxShadow: "0 -4px 16px rgba(0,0,0,0.12)",
        px: { xs: 2, md: 4 },
        py: { xs: 2, md: 2.5 },
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Typography sx={{ color: "#222", fontSize: { xs: "0.85rem", md: "0.95rem" }, flex: 1 }}>
        We use cookies to enhance your browsing experience, analyze site traffic and personalize
        content. Read our{" "}
        <MUILink
          component={RouterLink}
          to="/cookie-policy"
          sx={{ color: "#e3242b", fontWeight: 700, textDecoration: "underline" }}
        >
          Cookie Policy
        </MUILink>{" "}
        to learn more about how we use cookies.
      </Typography>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Button
          onClick={handleReject}
          variant="outlined"
          sx={{
            borderColor: "#ccc",
            color: "#333",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 3,
            "&:hover": { borderColor: "#999", backgroundColor: "#f5f5f5" },
          }}
        >
          Reject
        </Button>
        <Button
          onClick={handleCustomize}
          variant="outlined"
          sx={{
            borderColor: "#e3242b",
            color: "#e3242b",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 3,
            "&:hover": { borderColor: "#e3242b", backgroundColor: "#fdeceb" },
          }}
        >
          Customize
        </Button>
        <Button
          onClick={handleAccept}
          variant="contained"
          sx={{
            backgroundColor: "#e3242b",
            color: "#fff",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 1.5,
            px: 3,
            "&:hover": { backgroundColor: "#c61f26" },
          }}
        >
          Accept
        </Button>
      </Box>
    </Box>
  );
};

export default CookieConsent;
